import type {
  ErrorMessageType,
  ErrorObjectType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import { describeDisallowedCharacters } from "@freightsimple/ui-core/Display/disallowedCharacters";
import { unprintableBillOfLadingCharacters } from "@freightsimple/ui-core/Display/billOfLadingPrintability";
import { LineItemsTooLightQuestionBubble } from "../Bubbles/LineItemsTooLightQuestionBubble";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import { locationsErrorMessageForFreightClass } from "@/Shared/Locations/Validators/locationsErrorMessageForFreightClass";
import type { ShipmentLimits } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentLimits";
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";
import moment from "moment";
import { LineItemsTooManyHandlingUnitsQuestionBubble } from "../Bubbles/LineItemsTooManyHandlingUnitsQuestionBubble";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import ProhibitedCommodityDescriptions from "@/GeneratedFixtures/ProhibitedCommodityDescriptions.json";
import { dangerousGoodsIsClass1Explosive } from "@/Shared/DangerousGoods/Helpers/dangerousGoodsIsClass1Explosive";
import { dangerousGoodsRequiresTechnicalName } from "@/Shared/DangerousGoods/Helpers/dangerousGoodsRequiresTechnicalName";
import { maxDangerousNetExplosiveQuantityKg } from "@/Shared/DangerousGoods/Validators/dangerousGoodsLimits";
import { touchesCanada } from "@/Helpers/touchesCanada";
import {
  errorMessageForHeight,
  errorMessageForLength,
  errorMessageForWidth,
  qualifiesForLiftGateLongThinException,
} from "./lineItemsDimensionErrorMessages";

export interface LineItemErrors extends ErrorObjectType {
  handlingUnit: ErrorMessageType;
  description: ErrorMessageType;
  length: ErrorMessageType;
  width: ErrorMessageType;
  height: ErrorMessageType;
  weight: ErrorMessageType;
  isDangerous: ErrorMessageType;
  temperatureHandling: ErrorMessageType;
  isStackable: ErrorMessageType;
  freightClass: ErrorMessageType;
}

function errorMessageForDescriptionForProhibitedCommodity(
  description: string,
  prohibited: string[],
) {
  // Fall back to the bundled JSON fixture before the API responds. Once the backend has been
  // contacted, `prohibited` from the live limits is the source of truth.
  const list =
    prohibited.length > 0 ? prohibited : ProhibitedCommodityDescriptions;
  const lowered = list.map((d) => d.toLowerCase());
  if (lowered.includes(description.toLowerCase())) {
    return "This appears to be a prohibited item";
  } else {
    return undefined;
  }
}

export function errorMessageForDescription(
  description: string | undefined,
  limits: ShipmentLimits,
): ErrorMessageType {
  if (description === undefined || description === "") {
    return "Required";
  }

  if (description.length < limits.lineItem.minDescriptionLength) {
    return `Should be at least ${limits.lineItem.minDescriptionLength} characters`;
  }

  const disallowed = unprintableBillOfLadingCharacters(description);
  if (disallowed.length > 0) {
    return `${describeDisallowedCharacters(disallowed)}. Please retype it using standard keyboard characters.`;
  }

  return errorMessageForDescriptionForProhibitedCommodity(
    description,
    limits.prohibitedCommodityDescriptions,
  );
}

function errorMessageForWeight(
  weightPerHandlingUnit: number | undefined,
  totalWeightForShipment: number | undefined,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
  isLongThinExempt: boolean,
): ErrorMessageType {
  if (weightPerHandlingUnit === undefined) {
    return "Required";
  }

  if (weightPerHandlingUnit === 0) {
    return "Should not be zero";
  }

  if (
    totalWeightForShipment !== undefined &&
    totalWeightForShipment < limits.shipment.minWeightPounds
  ) {
    return (
      <>
        Too light <LineItemsTooLightQuestionBubble />
      </>
    );
  }
  if (weightPerHandlingUnit < limits.lineItem.minWeightPerHandlingUnitPounds) {
    return (
      <>
        Too light <LineItemsTooLightQuestionBubble />
      </>
    );
  }

  // Lift gate validation for weight
  const pickupRequiresLiftGate = pickupLocation.accessorials.includes(
    "LOGISTICS_LIFT_GATE_REQUIRED",
  );
  const deliveryRequiresLiftGate = deliveryLocation.accessorials.includes(
    "LOGISTICS_LIFT_GATE_REQUIRED",
  );
  const requiresLiftGate = pickupRequiresLiftGate || deliveryRequiresLiftGate;

  if (
    requiresLiftGate &&
    !isLongThinExempt &&
    weightPerHandlingUnit > limits.liftgate.maxWeightPerHandlingUnitPounds
  ) {
    return (
      <>
        Too heavy for lift gate{" "}
        <Tooltip
          variant="error"
          content={
            <Stack gap={2}>
              <T weight="medium">Too heavy for lift gate</T>
              <T>
                Handling units over{" "}
                {limits.liftgate.maxWeightPerHandlingUnitPounds}lb may be too
                heavy for some carrier&apos;s lift gates to handle.
              </T>
            </Stack>
          }
        />
      </>
    );
  }

  if (
    totalWeightForShipment &&
    totalWeightForShipment > limits.shipment.maxWeightPounds
  ) {
    return (
      <>
        Too heavy{" "}
        <Tooltip
          variant="error"
          content={
            <Stack gap={2}>
              <T weight="medium">Too heavy</T>
              <T>
                Total pallets must be no more than{" "}
                <T weight="medium">{limits.shipment.maxWeightPounds}lb</T>.
              </T>
              <T>
                Please make sure to enter the weight of each handling unit, not
                the combined weight of the shipment.
              </T>
            </Stack>
          }
        />
      </>
    );
  }

  return undefined;
}

function checkValid(errorMessages: LineItemErrors) {
  if (
    errorMessages.description ||
    errorMessages.handlingUnit ||
    errorMessages.height ||
    errorMessages.isDangerous ||
    errorMessages.isStackable ||
    errorMessages.length ||
    errorMessages.temperatureHandling ||
    errorMessages.weight ||
    errorMessages.width ||
    errorMessages.freightClass
  ) {
    return false;
  } else {
    return true;
  }
}

// Whether ProtectFromFreezing is unavailable for this pickup date because the
// pickup month falls outside the season window in which the backend offers it
// (April–October are disallowed; the window itself is owned by the backend and
// surfaced via the shipment-limits API as protectFromFreezingDisallowedMonths).
// Shared so the dropdown control and the validation message agree on one source
// of truth. Saved commodities have no pickupDate, so they are never out of season.
export function isProtectFromFreezingOutOfSeason(
  pickupDate: string | undefined,
  limits: ShipmentLimits,
): boolean {
  if (pickupDate === undefined) {
    return false;
  }
  // 1-indexed month so we can directly compare against the API's list.
  const monthValue = moment(pickupDate).month() + 1;
  return limits.temperatureHandling.protectFromFreezingDisallowedMonths.includes(
    monthValue,
  );
}

// We used to have error messages for some temperature handling (eg. frozen) but now we accept them
export function errorMessageForTemperatureHandling(
  // Saved commodities have no pickupDate
  pickupDate: string | undefined,
  temperatureHandling: TemperatureHandling,
  limits: ShipmentLimits,
): ErrorMessageType {
  if (
    temperatureHandling === TemperatureHandling.ProtectFromFreezing &&
    isProtectFromFreezingOutOfSeason(pickupDate, limits)
  ) {
    return `Not supported April-October`;
  }
  return undefined;
}

function errorMessageForHandlingUnit(
  handlingUnitType: string | undefined,
  totalHandlingUnitsForShipment: number,
  limits: ShipmentLimits,
) {
  if (handlingUnitType === undefined) {
    return "Required";
  }

  // The cap is on the shipment total, so every line item shows the error when it trips.
  if (totalHandlingUnitsForShipment > limits.shipment.maxHandlingUnits) {
    return (
      <>
        Too many handling units{" "}
        <LineItemsTooManyHandlingUnitsQuestionBubble
          maxHandlingUnits={limits.shipment.maxHandlingUnits}
        />
      </>
    );
  }

  if (limits.lineItem.disallowedHandlingUnitTypes.includes(handlingUnitType)) {
    // Preserve the specific messages the dashboard has shown historically so the UX stays
    // unchanged for the two values currently in the disallowed list.
    if (handlingUnitType === "Other") {
      return "Contact support to check this is shippable";
    }
    if (handlingUnitType === "Loose") {
      return "Not permitted. Must be properly package for shipping";
    }
    return "Handling unit type is not supported";
  }

  return undefined;
}

// The dangerous-goods dialog gates these fields when it is opened, but a line item can
// arrive complete-looking without it ever opening: applied saved commodities, drafts and
// duplicates from before the fields existed, or an address switched to Canada after the
// dialog was done. The server hard-rejects those on Get Quotes, so this check must catch
// them here, where it runs on the live locations.
export function errorMessageForDangerousGoods(
  lineItem: AddLineItemType,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
): ErrorMessageType {
  if (lineItem.isDangerous !== true) {
    return undefined;
  }

  const neq = lineItem.dangerousNetExplosiveQuantityKg;
  // The ceiling is physical (the item's own weight, capped by the heaviest shipment we
  // accept), so it applies on every lane.
  if (
    neq !== undefined &&
    neq >
      maxDangerousNetExplosiveQuantityKg(
        lineItem,
        limits.dangerousGoods.maxNetExplosiveQuantityKg,
      )
  ) {
    return "Net explosive quantity is too high. Edit the dangerous goods details";
  }

  const canadianTdgRules = touchesCanada(
    pickupLocation.address.countryCode,
    deliveryLocation.address.countryCode,
  );
  if (!canadianTdgRules) {
    return undefined;
  }

  if (
    dangerousGoodsIsClass1Explosive(lineItem.dangerousClassification) &&
    (neq === undefined || neq <= 0)
  ) {
    return "Net explosive quantity is required. Edit the dangerous goods details";
  }

  if (
    dangerousGoodsRequiresTechnicalName(lineItem.dangerousUnNumber) &&
    (lineItem.dangerousTechnicalName ?? "").trim() === ""
  ) {
    return "Technical name is required for this UN number. Edit the dangerous goods details";
  }

  return undefined;
}

export function lineItemsErrorMessages(
  pickupDate: string,
  li: AddLineItemType,
  totalWeightForShipment: number | undefined,
  totalHandlingUnitsForShipment: number,
  isFreightClassRequired: boolean,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
  limits: ShipmentLimits,
): { errorMessages: LineItemErrors; isValid: boolean } {
  // Computed once per line item: long-thin freight is hand-offloaded, so it is exempt from the
  // liftgate dimension and weight caps. Threaded into the dimension and weight checks below.
  const isLongThinExempt = qualifiesForLiftGateLongThinException(li, limits);

  const errorMessages = {
    handlingUnit: errorMessageForHandlingUnit(
      li.handlingUnitType,
      totalHandlingUnitsForShipment,
      limits,
    ),
    description: errorMessageForDescription(li.description, limits),
    length: errorMessageForLength(
      li,
      pickupLocation,
      deliveryLocation,
      limits,
      isLongThinExempt,
    ),
    width: errorMessageForWidth(
      li,
      pickupLocation,
      deliveryLocation,
      limits,
      isLongThinExempt,
    ),
    height: errorMessageForHeight(
      li,
      pickupLocation,
      deliveryLocation,
      limits,
      isLongThinExempt,
    ),
    weight: errorMessageForWeight(
      li.weightPerHandlingUnit,
      totalWeightForShipment,
      pickupLocation,
      deliveryLocation,
      limits,
      isLongThinExempt,
    ),
    isDangerous: errorMessageForDangerousGoods(
      li,
      pickupLocation,
      deliveryLocation,
      limits,
    ),
    temperatureHandling: errorMessageForTemperatureHandling(
      pickupDate,
      li.temperatureHandling,
      limits,
    ),
    isStackable: li.isStackable !== undefined ? undefined : "Required",
    freightClass: locationsErrorMessageForFreightClass(
      li.freightClass,
      isFreightClassRequired,
    ),
  };

  const isValid = checkValid(errorMessages);

  return { errorMessages, isValid };
}
